import React, { useState } from 'react';
import { 
  Autocomplete, 
  Button, 
  Grid, 
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormGroup,
  FormControlLabel,
  Checkbox,
  IconButton,
  InputAdornment,
  Box,
  Chip,
  Typography,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import FilterListIcon from '@mui/icons-material/FilterList';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import RecipeListCardPreview from '../RecipeListCardPreview';
import useIsMobile from '../../../hooks/useIsMobile';
import { useSelector } from 'react-redux';

// Mock data - replace with your actual data
const mockRecipes = [
  { id: 1, title: 'Pasta Carbonara', category: 'Italian', tags: ['pasta', 'quick'] },
  { id: 2, title: 'Chicken Curry', category: 'Indian', tags: ['chicken', 'spicy'] },
  // Add more recipes...
];

const categories = ['Italian', 'Indian', 'Chinese', 'American', 'Mexican'];
const tags = ['quick', 'easy', 'vegetarian', 'spicy', 'healthy', 'dessert'];

function LibraryPreview() {
  const navigate = useNavigate();
  const mobile = useIsMobile();

  const chefAdminRecipes = useSelector((state) => state.chefAdminRecipeData.chefAdminRecipes);

  const [openFilter, setOpenFilter] = useState(false);
  const [searchValue, setSearchValue] = useState(null);
  const [selectedFilters, setSelectedFilters] = useState({
    categories: [],
    tags: [],
  });

  const [activeFilters, setActiveFilters] = useState({
    categories: [],
    tags: [],
  });

  // const handleDeleteRecipe = (recipeId) => {
  //   console.log('Deleting recipe with ID:', recipeId);
  // };

  const handleFilterOpen = () => {
    setOpenFilter(true);
    setSelectedFilters(activeFilters);
  };

  const handleFilterClose = () => {
    setOpenFilter(false);
    setSelectedFilters(activeFilters);
  };

  const handleFilterApply = () => {
    setActiveFilters(selectedFilters);
    setOpenFilter(false);
  };

  const handleFilterChange = (type, value) => {
    setSelectedFilters(prev => ({
      ...prev,
      [type]: prev[type].includes(value)
        ? prev[type].filter(item => item !== value)
        : [...prev[type], value]
    }));
  };

  const handleFilterDelete = (type, value) => {
    setActiveFilters(prev => ({
      ...prev,
      [type]: prev[type].filter(item => item !== value)
    }));
  };

  return (
    <Grid 
      sx={{ 
        width: '100%', 
        display: 'flex', 
        flexDirection: 'column', 
        padding: 2,
        height: '100%',
        maxHeight: mobile ? '75dvh' : '67dvh',
      }}
    >
      {/* Search and Filter Section */}
      <Grid container display={'flex'} spacing={2} sx={{ mb: 2 }}>
        <Grid item flex={'6'} >
          <Autocomplete
            value={searchValue}
            onChange={(event, newValue) => setSearchValue(newValue)}
            options={chefAdminRecipes}
            getOptionLabel={(option) => option.title}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder="Search recipes..."
                InputProps={{
                  ...params.InputProps,
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
              />
            )}
          />
        </Grid>
        <Grid item flex={'1'} >
          <Button
            variant="outlined"
            onClick={handleFilterOpen}
            fullWidth
            sx={{ height: '100%', width: '100%' }}
          >
            <FilterListIcon />
          </Button>
        </Grid>
      </Grid>

      {/* Active Filters Display */}
      {(activeFilters.categories.length > 0 || activeFilters.tags.length > 0) && (
        <Box sx={{ mb: 2, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
          {activeFilters.categories.map((category) => (
            <Chip
              key={category}
              label={category}
              onDelete={() => handleFilterDelete('categories', category)}
              color="primary"
            />
          ))}
          {activeFilters.tags.map((tag) => (
            <Chip
              key={tag}
              label={tag}
              onDelete={() => handleFilterDelete('tags', tag)}
              color="secondary"
            />
          ))}
        </Box>
      )}

      {/* Recipe List */}
      <Grid 
        item 
        sx={{
          display: 'flex',
          justifyContent: 'flex-start',
          alignItems: 'center',
          width: '100%',
          flexGrow: 1,
          overflow: 'auto',
          flexDirection: 'column',
          height: '100%',
          '&::-webkit-scrollbar': {
            width: '0.4em'
          },
          '&::-webkit-scrollbar-track': {
            background: 'transparent'
          },
          '&::-webkit-scrollbar-thumb': {
            background: 'rgba(0,0,0,.1)',
            borderRadius: '24px'
          }
        }}
      >
        {
          chefAdminRecipes.map((item) => (
            <RecipeListCardPreview 
              recipeTitle={item?.title}
              id={item?._id}
              imageSrc={item?.picture}
            />
          ))
        }
      </Grid>

      {/* Filter Dialog */}
      <Dialog
        open={openFilter}
        onClose={handleFilterClose}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>
          Filter Recipes
          <IconButton
            onClick={handleFilterClose}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Typography variant="subtitle1" gutterBottom>
            Categories
          </Typography>
          <FormGroup>
            {categories.map((category) => (
              <FormControlLabel
                key={category}
                control={
                  <Checkbox
                    checked={selectedFilters.categories.includes(category)}
                    onChange={() => handleFilterChange('categories', category)}
                  />
                }
                label={category}
              />
            ))}
          </FormGroup>
          <Typography variant="subtitle1" gutterBottom sx={{ mt: 2 }}>
            Tags
          </Typography>
          <FormGroup>
            {tags.map((tag) => (
              <FormControlLabel
                key={tag}
                control={
                  <Checkbox
                    checked={selectedFilters.tags.includes(tag)}
                    onChange={() => handleFilterChange('tags', tag)}
                  />
                }
                label={tag}
              />
            ))}
          </FormGroup>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleFilterClose}>Cancel</Button>
          <Button onClick={handleFilterApply} variant="contained">
            Apply Filters
          </Button>
        </DialogActions>
      </Dialog>
    </Grid>
  );
}

export default LibraryPreview;